import React from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import { AbruptPitchChangesConfig } from "@/metrics/registry/configuration"
import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

const log = logger("AbruptPitchChangesMetric")

interface AbruptPitchChangesResultJson {
  abrupt_pitch_changes_count?: number
  significant_changes_count?: number
  abrupt_pitch_changes_per_minute?: number
  duration_seconds?: number
}

const CATEGORY = "abrupt_pitch_changes"

export class AbruptPitchChangesMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  aggregate_units = "/min"

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const formatTime = (s: number) =>
        `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`

      const resultJson = parseResultJson<AbruptPitchChangesResultJson>(mo)
      const totalCount =
        (mo.result?.count as number | undefined) ??
        resultJson?.abrupt_pitch_changes_count ??
        resultJson?.significant_changes_count ??
        0
      const ratePerMinute =
        (mo.result?.frequency as number | undefined) ??
        resultJson?.abrupt_pitch_changes_per_minute ??
        mo.float_value ??
        0
      const events = mo.subvalues_by_timestamp ?? []

      const handleSeekToTimestamp = (timestamp: number) => {
        simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
          time: timestamp,
        })
      }

      const BIN_SIZE = 3 // seconds
      const binsMap = new Map<number, typeof events>()
      for (const sv of events) {
        const bin = Math.floor(sv.start_offset / BIN_SIZE)
        if (!binsMap.has(bin)) {
          binsMap.set(bin, [])
        }
        binsMap.get(bin)!.push(sv)
      }
      const bins = Array.from(binsMap.entries()).sort(([a], [b]) => a - b)

      return (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
              <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                Total Events
              </div>
              <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                {totalCount}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-900/20 dark:border-amber-800">
              <div className="text-sm text-amber-600 font-medium mb-1 dark:text-amber-400">
                Rate
              </div>
              <div className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                {ratePerMinute.toFixed(2)}/min
              </div>
            </div>
          </div>

          {bins.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                Abrupt Pitch Changes
              </h3>
              <div className="space-y-3">
                {bins.map(([binIndex, binEvents]) => {
                  const binStart = binIndex * BIN_SIZE
                  const binEnd = (binIndex + 1) * BIN_SIZE
                  return (
                    <div
                      key={binIndex}
                      className="border border-gray-200 rounded-lg bg-white overflow-hidden dark:border-gray-700 dark:bg-gray-900"
                    >
                      <div
                        className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
                        onClick={() => handleSeekToTimestamp(binStart)}
                      >
                        <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                          {formatTime(binStart)} – {formatTime(binEnd)}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
                          {binEvents.length} event
                          {binEvents.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="divide-y divide-gray-100 dark:divide-blue-950">
                        {binEvents.map((sv, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 transition-colors cursor-pointer dark:hover:bg-gray-950"
                            onClick={() =>
                              handleSeekToTimestamp(sv.start_offset)
                            }
                          >
                            <span className="px-2 py-0.5 rounded text-xs font-semibold border bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800">
                              Abrupt Pitch Change
                            </span>
                            <span className="text-sm text-gray-600 font-mono dark:text-gray-400">
                              {formatTime(sv.start_offset)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {totalCount === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No abrupt pitch changes detected
            </div>
          )}
        </div>
      )
    } catch (error) {
      log.error("Error rendering abrupt pitch changes deep dive", error)
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      return (mo.subvalues_by_timestamp ?? []).map((item, idx) => ({
        id: `abrupt_pitch:${idx}`,
        start: item.start_offset,
        end: Math.max(item.end_offset, item.start_offset + 0.5),
        color: "rgba(255, 0, 0, 0.7)",
        drag: false,
        resize: false,
      }))
    } catch (e) {
      log.debug("Abrupt pitch changes data unavailable or could not parse", {
        error: e,
      })
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "per_minute",
      buildReviewedResultJson: ({ editedCount, score, durationSeconds }) => ({
        abrupt_pitch_changes_count: editedCount,
        abrupt_pitch_changes_per_minute: Number(score.toFixed(2)),
        duration_seconds:
          durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
      }),
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <AbruptPitchChangesConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(metricRecordData, onChange, textAreaRef)}
      </div>
    )
  }
}
